<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import InternalChatChannelMembers from './InternalChatChannelMembers.vue';
import InternalChatChannelCategories from './InternalChatChannelCategories.vue';

const props = defineProps({
  channel: { type: Object, required: true },
  categories: { type: Array, default: () => [] },
  members: { type: Object, default: () => ({ users: [], teams: [] }) },
  agents: { type: Array, default: () => [] },
  canManageCategories: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  performAction: { type: Function, required: true },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const dialog = ref(null);
let unmounting = false;
const onClose = () => {
  if (!unmounting) emit('close');
};
const confirmation = ref(null);
const action = ref('');
const channelName = ref(props.channel.name || '');
const categoryId = ref(props.channel.category_id || '');
onMounted(() => dialog.value.open());
onBeforeUnmount(() => {
  unmounting = true;
  confirmation.value?.close();
  dialog.value?.close();
});
const requestClose = () => {
  if (!props.isLoading) dialog.value?.close();
};
const openConfirmation = name => {
  if (props.isLoading) return;
  action.value = name;
  confirmation.value.open();
};
const confirm = async () => {
  if (!props.isLoading) await props.performAction(action.value);
};
const saveChannel = () =>
  props.performAction('update', {
    name: channelName.value.trim(),
    category_id: categoryId.value || null,
  });
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('INTERNAL_CHAT.CHANNEL_SETTINGS')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    confirm-before-close
    @close-requested="requestClose"
    @close="onClose"
  >
    <fieldset :disabled="isLoading" class="m-0 min-w-0 border-0 p-0">
      <div class="max-h-[min(60vh,38rem)] overflow-y-auto pe-1">
        <Input
          v-model="channelName"
          class="mb-4"
          :label="t('INTERNAL_CHAT.CHANNEL_NAME')"
        />
        <label class="mb-4 block text-sm font-medium text-n-slate-12">
          {{ t('INTERNAL_CHAT.CATEGORY') }}
          <Select
            v-model="categoryId"
            class="mt-1 !w-full [&_select]:w-full"
            :options="[
              { value: '', label: t('INTERNAL_CHAT.NO_CATEGORY') },
              ...categories.map(category => ({
                value: category.id,
                label: category.name,
              })),
            ]"
          />
        </label>
        <div class="mb-5 flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            :label="t('INTERNAL_CHAT.SAVE')"
            :is-loading="isLoading"
            @click="saveChannel"
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            color="slate"
            :label="t('INTERNAL_CHAT.MARK_UNREAD')"
            @click="performAction('markUnread')"
          />
        </div>
        <InternalChatChannelMembers
          :can-manage="channel.channel_type !== 'dm'"
          :members="members"
          :agents="agents"
          :perform-action="performAction"
        />
        <InternalChatChannelCategories
          v-if="canManageCategories"
          :categories="categories"
          :perform-action="performAction"
        />
      </div>
    </fieldset>
    <p v-if="error && !action" class="m-0 text-sm text-n-ruby-11" role="alert">
      {{ error }}
    </p>
    <template #footer>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            color="slate"
            :disabled="isLoading"
            :label="t('INTERNAL_CHAT.ARCHIVE')"
            @click="openConfirmation('archive')"
          />
          <Button
            type="button"
            variant="ghost"
            color="ruby"
            :disabled="isLoading"
            :label="t('INTERNAL_CHAT.DELETE')"
            @click="openConfirmation('delete')"
          />
        </div>
        <Button
          type="button"
          variant="faded"
          color="slate"
          :disabled="isLoading"
          :label="t('INTERNAL_CHAT.CLOSE')"
          @click="requestClose"
        />
      </div>
    </template>
    <Dialog
      ref="confirmation"
      type="alert"
      width="md"
      :title="
        t(
          action === 'delete' ? 'INTERNAL_CHAT.DELETE' : 'INTERNAL_CHAT.ARCHIVE'
        )
      "
      :description="
        t(
          action === 'delete'
            ? 'INTERNAL_CHAT.CONFIRM_DELETE_CHANNEL'
            : 'INTERNAL_CHAT.CONFIRM_ARCHIVE_CHANNEL'
        )
      "
      :confirm-button-label="
        t(
          action === 'delete' ? 'INTERNAL_CHAT.DELETE' : 'INTERNAL_CHAT.ARCHIVE'
        )
      "
      :cancel-button-label="t('INTERNAL_CHAT.CANCEL')"
      :is-loading="isLoading"
      :confirm-before-close="isLoading"
      @confirm="confirm"
      @close="action = ''"
    >
      <p v-if="error" class="m-0 text-sm text-n-ruby-11" role="alert">
        {{ error }}
      </p>
    </Dialog>
  </Dialog>
</template>
